//
// # Ruler / Measurement Mode
//
// A measurement tool for layout views.
// Press 'm' to enter ruler mode.
// First click anchors the start point, second click defines the endpoint.
// Displays a line between the two points with a distance label in nanometers.
// Multiple rulers can be placed. Escape clears all rulers and exits.
//

// Local Imports
import { model, graphics } from "GenAlphaModel";
import type { InteractionHost } from "../interaction_host";
import { ModeHandler } from "./base";
import { DrawTo } from "DrawCore/src/draw";
import * as style from "DrawCore/src/style";

// Ruler line color: cyan to stand out from design elements
const RULER_COLOR = style.Color.new(0, 200, 255, 1);

// Style for the ruler line
const rulerLineStyle: style.ShapeStyle = {
  cap: "round",
  join: "round",
  linewidth: 2,
  fill: style.Special.None,
  stroke: RULER_COLOR,
};

// Style for the endpoint marker crosshairs
const markerStyle: style.ShapeStyle = {
  cap: "round",
  join: "round",
  linewidth: 2,
  fill: style.Special.None,
  stroke: RULER_COLOR,
};

// Style for the distance label text
const labelStyle: style.TextStyle = {
  family: style.THE_FONT,
  style: "normal",
  weight: 700,
  size: 14,
  fill: RULER_COLOR,
  stroke: style.Special.None,
};

// Marker crosshair half-size in schematic units
const MARKER_SIZE = 10;

// A completed ruler measurement
interface Ruler {
  anchor: model.Point;
  endpoint: model.Point;
}

// Shared state for the ruler tool, persists across mode transitions
class RulerState {
  readonly completed: Array<Ruler> = [];
}

// Format a distance in nanometers for display
function formatDistance(distNm: number): string {
  if (distNm >= 1_000_000) {
    return `${(distNm / 1_000_000).toFixed(3)} mm`;
  }
  if (distNm >= 1_000) {
    return `${(distNm / 1_000).toFixed(3)} um`;
  }
  return `${distNm.toFixed(1)} nm`;
}

// Calculate distance between two points
function distance(a: model.Point, b: model.Point): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  return Math.sqrt(dx * dx + dy * dy);
}

// Draw a crosshair marker at the given point
function drawMarker(
  to: DrawTo,
  point: model.Point,
  size: number,
  scale: number
): void {
  const scaledStyle = {
    ...markerStyle,
    linewidth: markerStyle.linewidth / scale,
  };
  // Horizontal line
  to.path(
    graphics.Path.fromPartial({
      points: [
        { x: point.x - size, y: point.y },
        { x: point.x + size, y: point.y },
      ],
    }),
    scaledStyle
  );
  // Vertical line
  to.path(
    graphics.Path.fromPartial({
      points: [
        { x: point.x, y: point.y - size },
        { x: point.x, y: point.y + size },
      ],
    }),
    scaledStyle
  );
}

// Draw a single ruler line, endpoint markers, and distance label
function drawRuler(
  to: DrawTo,
  anchor: model.Point,
  endpoint: model.Point,
  scale: number
): void {
  // Scale marker size inversely with zoom so they stay visually consistent
  const markerSize = MARKER_SIZE / scale;

  // Draw the ruler line
  to.path(
    graphics.Path.fromPartial({
      points: [anchor, endpoint],
    }),
    { ...rulerLineStyle, linewidth: rulerLineStyle.linewidth / scale }
  );

  // Draw endpoint markers
  drawMarker(to, anchor, markerSize, scale);
  drawMarker(to, endpoint, markerSize, scale);

  // Compute distance and label position
  const dist = distance(anchor, endpoint);
  const midpoint: model.Point = {
    x: (anchor.x + endpoint.x) / 2,
    y: (anchor.y + endpoint.y) / 2,
  };

  // Format the distance string with dx/dy components
  const dx = Math.abs(endpoint.x - anchor.x);
  const dy = Math.abs(endpoint.y - anchor.y);
  const label = `${formatDistance(dist)}  (dx: ${formatDistance(
    dx
  )}, dy: ${formatDistance(dy)})`;

  // Draw the label text
  // Offset the label slightly above the midpoint so it doesn't overlap the line
  const textOffset = 8 / scale;
  const textLoc: model.Point = {
    x: midpoint.x,
    y: midpoint.y + textOffset,
  };

  const textData = graphics.Text.fromPartial({
    text: label,
    loc: textLoc,
    height: labelStyle.size / scale,
    alignment: "center",
    baseline: "bottom",
    rotation: 0,
    hidden: false,
  });

  to.text(
    textData,
    { ...labelStyle, size: labelStyle.size / scale },
    { xform: model.Xform.default() }
  );
}

// Draw all completed rulers plus an optional in-progress one
function drawAllRulers(
  to: DrawTo,
  state: RulerState,
  scale: number,
  inProgress?: { anchor: model.Point; endpoint: model.Point }
): void {
  for (const ruler of state.completed) {
    drawRuler(to, ruler.anchor, ruler.endpoint, scale);
  }
  if (inProgress) {
    drawRuler(to, inProgress.anchor, inProgress.endpoint, scale);
  }
}

// Update the status message with the count of placed rulers
function updateStatus(editor: InteractionHost, state: RulerState): void {
  const count = state.completed.length;
  if (count === 0) {
    editor.overlays.setStatusMessage(
      "Ruler: click to place start point",
      "ruler"
    );
  } else {
    editor.overlays.setStatusMessage(
      `Ruler: ${count} placed, click for next (Esc to clear)`,
      "ruler"
    );
  }
}

//
// Phase 1: Waiting for first click to place anchor point
//
export class RulerAnchor extends ModeHandler {
  readonly state: RulerState;

  constructor(readonly editor: InteractionHost, state?: RulerState) {
    super();
    this.state = state ?? new RulerState();
    updateStatus(editor, this.state);
  }

  static enter(editor: InteractionHost): RulerAnchor {
    const mode = new RulerAnchor(editor);
    editor.setMode(mode);
    return mode;
  }

  // On mouse up, place the anchor and transition to Phase 2
  override handleMouseUp = () => {
    const { editor, state } = this;
    const anchor = { ...editor.uiState.mousePos.sch };
    return editor.setMode(new RulerMeasure(editor, anchor, state));
  };

  override abort = () => {
    this.editor.overlays.clearStatusMessage("ruler");
    return this.editor.goUiIdle();
  };

  // Draw completed rulers
  override draw = (to: DrawTo): void => {
    if (this.state.completed.length === 0) return;
    const scale = this.editor.canvas.worldTransforms.schToPage.scale;
    drawAllRulers(to, this.state, scale);
  };
}

//
// Phase 2: Anchor placed, tracking endpoint with live drawing
//
export class RulerMeasure extends ModeHandler {
  endpoint: model.Point;

  constructor(
    readonly editor: InteractionHost,
    readonly anchor: model.Point,
    readonly state: RulerState
  ) {
    super();
    this.endpoint = { ...anchor };
    editor.overlays.setStatusMessage(
      "Ruler: click to place end point",
      "ruler"
    );
  }

  // Update endpoint to current mouse position and trigger redraw
  override handleMouseMove = () => {
    this.endpoint = { ...this.editor.uiState.mousePos.sch };
    return this.editor.requestRedraw();
  };

  // Finalize endpoint, add to completed list, go back to anchor mode for next ruler
  override handleMouseUp = () => {
    this.endpoint = { ...this.editor.uiState.mousePos.sch };
    this.state.completed.push({
      anchor: this.anchor,
      endpoint: this.endpoint,
    });
    return this.editor.setMode(new RulerAnchor(this.editor, this.state));
  };

  override abort = () => {
    this.editor.overlays.clearStatusMessage("ruler");
    return this.editor.goUiIdle();
  };

  // Draw all completed rulers plus the in-progress one
  override draw = (to: DrawTo): void => {
    const scale = this.editor.canvas.worldTransforms.schToPage.scale;
    drawAllRulers(to, this.state, scale, {
      anchor: this.anchor,
      endpoint: this.endpoint,
    });
  };
}
